﻿import $ from 'jquery';
import Element from '../utilities/element';
import KeyCode from "../utilities/keycode";
import _ from 'underscore';
import { OnFilteredEventObject } from "./filteritems";

declare global {
  interface JQuery {
    multiselectdropdown: (option, args) => any;
  }
}

export class MultiSelectDropdown {
  public static get wireup() {
    return '[data-multiselect]';
  }

  private static wireupDocument: Function = _.once(() => {
    $(document).on('change.rustig.multiselect', '[data-ms-item] [data-ms-checkbox]', function () {
      MultiSelectDropdown.findFromCheckbox($(this)).onItemChecked();
    });
  });

  public static findFromCheckbox($checkbox: JQuery): MultiSelectDropdown {
    let popoverId = $checkbox.closest('[data-ms-popover').data('ms-popover');
    let $multiSelect = $('[data-popover="' + popoverId + '"]').closest('[data-multiselect]');
    let multiSelect = <MultiSelectDropdown>$multiSelect.data('rustig.multiselectdropdown');

    return multiSelect;
  }

  private $element: JQuery;
  private $realInput: JQuery;
  private $popover: JQuery;
  private $popoverTrigger: JQuery;
  private $userFacingInput: JQuery;
  private $inputDisplay: JQuery;
  private $filterInput: JQuery;
  private $filterButton: JQuery;
  private $filterButtonIcon: JQuery;
  private $clearFilters: JQuery;
  private $itemsContainer: JQuery;
  private filters: Filter;
  private popoverId: string;
  private emptyText: string;

  private get emptyFilter(): Filter {
    return {
      showSelectedOnly: false,
      keywordSearch: null
    };
  }

  private get isButtonRendered(): boolean {
    return this.$userFacingInput.is('[data-ms-as-button]');
  }

  private get errorPopover(): any {
    if (!this.$userFacingInput.is('.input-validation-error'))
      return null;
    return this.$popoverTrigger.data('rustig.popover.error');
  }

  public get $userFacingElement(): JQuery {
    return this.$userFacingInput;
  }

  public get $valueElement(): JQuery {
    return this.$realInput;
  }

  public value(value?: any): any | void {
    if (value !== undefined && value) {
      this.$realInput.val(value);
      this.onValueSet();
    }
    else
      return this.$realInput.val();
  }

  public text(text?: any): any | void {
    if (text !== undefined) {
      this.$inputDisplay.text(text);
    }
    else
      return this.$inputDisplay.text();
  }

  public name(): string {
    return this.$realInput.attr('name');
  }

  public constructor($element: JQuery, options: any) {
    MultiSelectDropdown.wireupDocument();

    if (!$element || $element.length != 1)
      throw new Error('You must provide an element to Rustig.MultiSelectDropdown');

    $element.data('mappable', 'on');
    this.$element = $element;
    this.filters = this.emptyFilter;
    this.cacheElements();
    this.handleReadonly();
    this.onSelectionChanged();
    this.wireupEvents();
  }

  private cacheElements(): void {
    this.$realInput = this.$element.find('[data-ms-real-input]');
    this.$userFacingInput = this.$element.find('[data-ms-user-facing-input]');
    this.$inputDisplay = this.$element.find('[data-ms-input-display]');
    this.$popoverTrigger = this.$element.find('[data-popover]');

    // force the popover to be shown so we can cache it
    this.$popoverTrigger.popover('open').popover('close');

    this.popoverId = this.$popoverTrigger.data('popover');
    this.$popover = $('[data-ms-popover="' + this.popoverId + '"]');
    this.$filterInput = this.$popover.find('[data-ms-filter-input]');
    this.$filterButton = this.$popover.find('[data-ms-filter-button]');
    this.$filterButtonIcon = this.$popover.find('[data-ms-filter-button-icon]');
    this.$clearFilters = this.$popover.find('[data-ms-clear-filters]');
    this.$itemsContainer = this.$popover.find('[data-ms-items]');
    this.emptyText = this.$userFacingInput.data("ms-empty-text");
  }

  private handleReadonly(): void {
    if (this.$popoverTrigger.isReadonly())
      this.$popoverTrigger.popover('disable');
  }

  private wireupEvents(): void {
    this.$userFacingInput.on('focus', (e: JQueryEventObject) => {
      this.openErrorPopover(e);
    });

    this.$userFacingInput.on('blur', (e: JQueryEventObject) => {
      this.closeErrorPopover(e);
    });

    this.$popoverTrigger.on('shown.rustig.popover', (e: any, data: any) => {
      if (data.channel.toString() !== this.popoverId)
        return;

      this.focusKeywordFilter();
      this.openErrorPopover(e as JQueryEventObject);
    });

    this.$popoverTrigger.on('hide.rustig.popover', (e) => {
      this.$filterInput.val(null).trigger('input');
      this.$filterButton.removeClass('active');

      this.filters.showSelectedOnly = false;
      this.filters.keywordSearch = null;
      this.onFilterChanged();
    });

    this.$filterButton.on('click.rustig.multiselect', () => {
      this.filters.showSelectedOnly = !this.filters.showSelectedOnly;

      this.focusKeywordFilter();
      this.onFilterChanged();
    });

    this.$filterInput.on('onfiltered.rustig.filteritems', (e: any, data: OnFilteredEventObject) => {
      this.showOrHideItems(data.$filtered, false);
      this.showOrHideItems(data.$matched, true);
    });

    this.$filterInput.on('input.rustig.multiselect', () => {
      this.filters.keywordSearch = new Element($(this.$filterInput)).value();
      this.filters.showSelectedOnly = false;
      this.onFilterChanged();
    });

    this.$clearFilters.on('click.rustig.multiselect', () => {
      this.filters = this.emptyFilter;
      new Element(this.$filterInput).value(null);
      this.$filterInput.trigger('input');

      this.deselectAllItems();
      this.focusKeywordFilter();
      this.onFilterChanged();
    });

    this.$popover.on('keypress.rustig.multiselect', (e: JQueryEventObject) => {
      if (e.keyCode != KeyCode.Enter)
        return true;

      this.$popoverTrigger.popover('close');
      // todo: up\down arrow support
      // todo: open flyout on keypress
    });
  }

  private onSelectionChanged(): void {
    let $selectedItems = this.$popover.find(':checked').closest('[data-ms-item]');

    let selections = _.map<HTMLElement, any>($.makeArray($selectedItems), function (el: HTMLElement) {
      let $el: JQuery = $(el);
      return {
        value: $el.data('value'),
        text: $el.data('text')
      }
    });

    this.setRealInput(selections);
    this.formatDisplay(selections);
  }

  private onFilterChanged(): void {
    let $checkboxes = this.$itemsContainer.find('[data-ms-checkbox]');
    let $uncheckedMatchedItems = $checkboxes.filter(':not(:checked)').closest('[data-ms-item][data-fi-matched]');
    let shouldShowUnchecked = !this.filters.showSelectedOnly;

    this.showOrHideItems($uncheckedMatchedItems, shouldShowUnchecked);
    this.styleFilterButton();
  }

  private deselectAllItems(): void {
    let $checkboxes = this.$itemsContainer.find('[data-ms-checkbox]');
    let $checkedCheckboxes = $checkboxes.filter(':checked').closest('[data-ms-item]').find('[data-ms-checkbox]');

    let elements = Element.fromElements($checkedCheckboxes);
    elements.forEach(x => x.suppressValueChange(false));

    this.onSelectionChanged();
  }

  private showOrHideItems($items: JQuery, shouldShow: boolean): void {
    if (shouldShow) {
      $items.removeClass('display-none').removeAttr('data-ms-hidden');
    }
    else {
      $items.addClass('display-none').attr('data-ms-hidden', 'on');
    }
  }

  private focusKeywordFilter(): void {
    this.$filterInput.focus();
  }

  private styleFilterButton(): void {
    let isActive = this.filters.showSelectedOnly;

    if (isActive) {
      this.$filterButton.addClass("color-primary");
      this.$filterButtonIcon.addClass("icon-filter-active");

      this.$filterButtonIcon.removeClass("icon-filter");
    }
    else {
      this.$filterButtonIcon.addClass("icon-filter");

      this.$filterButton.removeClass("color-primary");
      this.$filterButtonIcon.removeClass("icon-filter-active");
    }
  }

  private openErrorPopover(e: JQueryEventObject) {
    let errorPopover = this.errorPopover;
    if (errorPopover)
      errorPopover.open(e);
  }

  private closeErrorPopover(e: JQueryEventObject) {
    let errorPopover = this.errorPopover;
    if (errorPopover)
      errorPopover.close(e);
  }

  private onValueSet(): void {
    let selectedValues = (<string>this.value()).split(',');
    let $items = this.$popover.find('[data-ms-item]');
    let $currentlySelected = $items.find('[data-ms-checkbox]:checked');
    let unselectAll = () => {
      Element.fromElements($currentlySelected).forEach(x => x.value(false));
    }
    let checkSelectedItems = () => {
      selectedValues.forEach(x => {
        let $selectedItem = $items.filter(`[data-value="${x}"]`);
        if ($selectedItem.length > 0)
          new Element($selectedItem.find('[data-ms-checkbox]')).value(true);
      });
    }

    unselectAll();
    checkSelectedItems();
    this.onSelectionChanged();
  }

  private setRealInput(selections: any): void {
    let values = selections.map(function (x) {
      return x.value
    }).join(',');

    new Element(this.$realInput).value(values);
  }

  private formatDisplay(selections: any): void {
    let selectedCount = selections.length;
    let display = this.emptyText;

    if (selectedCount >= 2) {
      var firstItem = selections[0].text;
      var remainder = selectedCount - 1;
      display = `${firstItem} (+${remainder})`;
    }
    else if (selectedCount == 1) {
      display = selections[0].text;
    }

    this.$inputDisplay.text(display)
      .toggleClass('color-placeholder', selectedCount == 0);
  }

  public onItemChecked() {
    this.onSelectionChanged();
  }
}

interface Filter {
  showSelectedOnly: boolean,
  keywordSearch: string
}

function Plugin(option, args) {
  return this.each(function () {
    let $this = $(this);
    let data = $this.data('rustig.multiselectdropdown');
    let options = typeof option == 'object' && option;

    if (!data) {
      data = new MultiSelectDropdown($this, options);
      $this.data('rustig.multiselectdropdown', data);
    }
    if (typeof option == 'string')
      data[option](args);
  });
}

$.fn.multiselectdropdown = Plugin;
